/* global SearchVideosEnabled NODE_ENV */

/* eslint-disable import/prefer-default-export, import/no-unused-modules */
import _ from 'lodash';
import Regex from 'helpers/Regex';
import { SITE_PLATFORM_MOBILE, SITE_PLATFORM_TABLET } from 'helpers/BreakpointHelper';
import { isPreviewPass } from 'helpers/PlayerHelper';
import { getMVPDCookie, isClip } from 'helpers/ContentHelper';
import { getLocalStorageItem, setLocalStorageItem, getCookie, removeCookie } from 'helpers/Storage';
import { tveEnabled, isNationEntitled } from './ConfigHelper';
import { MVPD_MANUAL_LOGOUT } from './AuthHelper';

/* User Attributes */
const APP_SECTION_VISITED = 'app_section_visited';
const COLLECTION_VISITED = 'collection_visited';
const CURRENT_PAGE_URL = 'current_page_url';
const AUTH_STATE = 'authentication_state';
const FOX_PROFILE_STATE = 'fox_profile_state';
const FIRST_SITE_VISIT = 'is_first_visit';
const NUM_VISITS = 'num_visits';
const VIDEO_50_PERCENT_WATCHING = '50_percent_watching';

// Auth States
const NOT_AUTHED = 'never attempted';
export const ATTEMPTED_AUTH = 'authentication attempted';
export const SUCCESS_AUTH = 'authentication successful';
export const SIGN_OUT_ATTEMPTED_AUTH = 'sign out attempted';
export const SIGN_OUT_SUCCESS_AUTH = 'manually signed out';
export const AUTH_EXPIRED = 'automatically signed out';
// Fox Profile States
const FP_NEVER_ATTEMPTED = 'never attempted';

// local storage
const PERCENT_WATCHING = 'PERCENT_WATCHING';
const APP_VISITED = 'APP_VISITED';
const FP_STATE = 'FP_STATE';
const LOGGED_OUT_AUTH = 'LOGGED_OUT_AUTH';

/**
 * Activate optimizely experiment(s)
 * If no param provided it manually activates all optimizely experiments
 * WARNING: The experiment must be in Manual mode for activating all to work!
 * @param {Array} [keys] - experiment ids
 */

export const activateOptimizely = keys => {
  window.optimizely = window.optimizely || [];

  if (keys) {
    keys.forEach(key => {
      window.optimizely.push(['activate', key]);
    });
  } else {
    const experiments = _.get(window, 'optimizely.data.state.activeExperiments', []);
    experiments.forEach(experimentId => {
      window.optimizely.push(['activate', experimentId]);
    });
  }
};

export const getOptimizely = () => _.get(window, 'optimizely');
export const getOptimizelyExperiment = experimentName => _.get(window, experimentName, null);
const getOptimizelyBehavior = () => _.get(getOptimizely(), 'get', _.noop)('behavior');
export const getOptimizelyPlayerExperiments = () => _.get(window, 'playerABTest');
export const getOptimizelyPreviewPassExperiments = () =>
  _.get(window, 'previewPassExperiments', {});
export const getOptimizelyRedesignExperiments = () => _.get(window, 'redesignExperiments', {});
export const getOptimizelyRedesignDisabledPages = () =>
  _.get(getOptimizelyRedesignExperiments(), 'disabledPages', []);

export const getOptimizelyFakeCollectionData = () =>
  _.get(window, 'optimizelyFakeCollectionData', {});
export const getOptimizelyNationFooterUpsell = () => _.get(window, 'optimizelyRenderFooterUpsell');
export const getOptimizelyRedesignCollections = () =>
  _.get(window, 'optimizelyRedesignCollections', false);
export const getOptimizelyResultsOverrideEnabled = () => window?.optimizelyResultsOverride;
export const getOptimizelySearchVideoEnabled = () =>
  typeof window.searchVideosEnabled !== 'undefined'
    ? window.searchVideosEnabled
    : SearchVideosEnabled;

export const triggerWatchInitVariables = (videoFormat, showCode, videoType, network) => {
  window.video_content_type = videoFormat;
  window.video_series_id = showCode;
  window.video_content_length_format = isClip(videoType) ? 'short-form' : 'long-form';
  window.video_network = network;
  window.optimizely = window?.optimizely || [];
  window.optimizely.push({
    type: 'page',
    pageName: 'watch_page_with_window_init_variables',
    tags: {
      page_url: window.location.href,
      page_title: document.title,
      video_content_type: window.video_content_type,
      video_series_id: window.video_series_id,
      video_content_length_format: window.video_content_length_format,
      video_network: window.video_network,
    },
  });
};

export const deactivateWatchInitVariables = () => {
  if (window?.video_content_type) window.video_content_type = null;
  if (window?.video_series_id) window.video_series_id = null;
  if (window?.video_content_length_format) window.video_content_length_format = null;
  if (window?.video_network) window.video_network = null;
};

/*
 * Optimizely User Attributes
 * Variables added to window to allow access in the Optimizely dashboard
 */

const setUserAttributes = ({ name, value }) => {
  window.optimizely = window.optimizely || [];
  // Default attribute value to true, but allow for false values if explicitly provided
  const attributeValue = value === false ? value : value || true;
  const attrData = {
    type: 'user',
    attributes: { [name]: attributeValue },
  };
  window.optimizely.push(attrData);
};

export const getSdkUserAttributes = ({ breakpoint, seriesId = '' }) => {
  let deviceType = '';
  switch (breakpoint) {
    case SITE_PLATFORM_TABLET:
      deviceType = 'tablet';
      break;
    case SITE_PLATFORM_MOBILE:
      deviceType = 'mobile';
      break;
    default:
      deviceType = 'desktop';
      break;
  }
  return {
    page_url: window.location.href,
    series_id: seriesId,
    is_debug_build: NODE_ENV !== 'production',
    is_trialist_subscriber: !!isNationEntitled(),
    device_type: deviceType,
    browser_name_version: window.navigator.userAgent,
    user_cookies: document.cookie,
  };
};

export const userAttributeEvents = () => ({
  home: () => {
    setUserAttributes({ name: `${APP_SECTION_VISITED}_home` });
  },
  // Browse Event is for both home & movies pages
  browse: () => {
    setUserAttributes({ name: `${APP_SECTION_VISITED}_browse` });
  },
  currentPageUrl: () => {
    const url = window.location.href;
    setUserAttributes({ name: CURRENT_PAGE_URL, value: url });
  },
  live: () => {
    setUserAttributes({ name: `${APP_SECTION_VISITED}_live_tv` });
  },
  settings: () => {
    setUserAttributes({ name: `${APP_SECTION_VISITED}_settings` });
  },
  collectionItem: collectionName => {
    const formattedName = collectionName.toLowerCase().replace(/[^a-zA-Z0-9_\s]/g, '');
    const apiName = formattedName.replace(Regex.SPACE, '_');
    setUserAttributes({ name: `${COLLECTION_VISITED}_${apiName}` });
  },
  foxProfileState: profileState => {
    if (profileState === FP_NEVER_ATTEMPTED || !profileState) {
      const lastState = getLocalStorageItem({ key: FP_STATE });
      if (lastState) {
        setUserAttributes({
          name: FOX_PROFILE_STATE,
          value: lastState,
        });
        return setLocalStorageItem({ key: FP_STATE, value: profileState });
      }
    }
    setUserAttributes({
      name: FOX_PROFILE_STATE,
      value: profileState,
    });
    return setLocalStorageItem({ key: FP_STATE, value: profileState });
  },
  authState: authState => {
    if (authState === SUCCESS_AUTH) {
      removeCookie({ key: MVPD_MANUAL_LOGOUT, options: { path: '/' } });
      setLocalStorageItem({ key: LOGGED_OUT_AUTH, value: false });
    }
    if (authState === SIGN_OUT_SUCCESS_AUTH)
      setLocalStorageItem({ key: LOGGED_OUT_AUTH, value: true });
    if (authState === NOT_AUTHED && getLocalStorageItem({ key: LOGGED_OUT_AUTH })) {
      const manualLogout = getCookie({ name: MVPD_MANUAL_LOGOUT });
      const value = !manualLogout ? AUTH_EXPIRED : SIGN_OUT_SUCCESS_AUTH;
      return setUserAttributes({ name: AUTH_STATE, value });
    }
    return setUserAttributes({ name: AUTH_STATE, value: authState });
  },
  appVisitState: () => {
    const appVisited = getLocalStorageItem({ key: APP_VISITED });
    setLocalStorageItem({ key: APP_VISITED, value: true });
    return setUserAttributes({ name: FIRST_SITE_VISIT, value: !appVisited });
  },
  numVisits: () => {
    const numVisits = _.get(
      getOptimizelyBehavior(),
      'query',
      _.noop,
    )({
      version: '0.2',
      filter: [
        {
          field: ['type'],
          value: 'pageview',
        },
        {
          field: ['name'],
          value: 'all_nation_pages',
          comparator: 'eq',
        },
      ],
    });
    const sessionIndex = _.get(numVisits, '0.session_index', -1) + 1;
    if (sessionIndex > 0) {
      return setUserAttributes({ name: NUM_VISITS, value: sessionIndex });
    }
    return null;
  },
  video50PercentWatching: (hasWatched50Percent = false) => {
    const stored50PercentWatched = getLocalStorageItem({ key: PERCENT_WATCHING });
    if (stored50PercentWatched) {
      return setUserAttributes({ name: VIDEO_50_PERCENT_WATCHING, value: true });
    }
    if (hasWatched50Percent) {
      setLocalStorageItem({ key: PERCENT_WATCHING, value: true });
      return setUserAttributes({ name: VIDEO_50_PERCENT_WATCHING, value: true });
    }
    return setUserAttributes({ name: VIDEO_50_PERCENT_WATCHING, value: false });
  },
});

/**
 * Sets user attributes for Optimizely on app load
 * @param {Boolean} isAuthenticated - user authentication status
 * @param {Boolean} foxProfileAccount - user is logged into fox profile account
 */

export const setInitOptimizelyAttributes = (isAuthenticated, foxProfileAccount) => {
  userAttributeEvents().appVisitState();
  userAttributeEvents().currentPageUrl();

  const mvpdCookie = getMVPDCookie();
  const mvpdName = _.get(mvpdCookie, 'mvpd');
  const isPreviewPassMVPD = isPreviewPass(mvpdName);
  if (isAuthenticated && !isPreviewPassMVPD) {
    userAttributeEvents().authState(SUCCESS_AUTH);
  } else {
    userAttributeEvents().authState(NOT_AUTHED);
  }
  if (foxProfileAccount) {
    userAttributeEvents().foxProfileState();
  } else {
    userAttributeEvents().foxProfileState(FP_NEVER_ATTEMPTED);
  }
};

/*
 * Optimizely Experiment Helpers
 * Variables added to window to allow access in the Optimizely dashboard
 */

export const modifyBreakpoints = (breakpoints, screen) => {
  if (window.modifyBreakpoints && window.newBreakpoints) {
    const screenBreakpoints = _.get(window.newBreakpoints, screen, false);
    if (screenBreakpoints) {
      return _.merge(breakpoints, screenBreakpoints);
    }
  }
  return breakpoints;
};

export const modifyTileImage = () => {
  let currentScreen = window.location.pathname.replace(/\//g, '');
  currentScreen = currentScreen || 'home';

  const modifyTileImages = _.get(window, 'modifyTileImages', null);
  const modifiedScreen = _.get(modifyTileImages, currentScreen, null);
  const modifiedImageType = _.get(modifiedScreen, 'imageType', null);

  if (modifyTileImages && modifiedScreen && modifiedImageType) {
    return modifiedImageType;
  }
  return null;
};

export const replaceScreenUrl = (pathKey, screenName) => {
  if (window.modifyScreenUrl) {
    return window.modifyScreenUrl(pathKey, screenName);
  }
  return false;
};

export const showShowsTileCTAs = () => !!window.modifyShowsPageTiles;
export const modifyShowsPageTileCTAS = () => _.get(window, 'modifyShowsPageTiles.watchText', false);

export const useMVPDLoginProfile = () => {
  if (tveEnabled()) return _.get(window, 'optimizelyUseMVPDLoginProfile', false);
  return false;
};

export const optimizelyLanguageStrings = () => _.get(window, 'optimizelyLanguageStrings', {});

export const getOptimizelyUnlockedVideoPage = () => {
  const exemptValue = _.get(window, 'expmt_unlocked_video_page', null);
  return exemptValue === 'treatment';
};

export const getOptimizelyRemovedSeries = seriesName => {
  const isValidSeries = _.get(window, 'check_optimizely_removed_series', () => true);
  return isValidSeries(seriesName);
};
